import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { ConfirmContext } from '../../contexts/confirm.context';
import { IBar } from '../../models/IBars';
import { ICustomer, ICustomerReport } from '../../models/ICustomers';
import BarsService from '../../services/bar/bar.service';
import CustomerDetailsPage from '../../pages/CustomerDetails/CustomerDetails.page';
import CustomerService from '../../services/customer/customer.service';

export default function CustomerDetailsContainer() {
    const history = useHistory();
    const params = useParams<any>();
    const [customer, setCustomer] = useState<ICustomer>();
    const [report, setReport] = useState<ICustomerReport>();
    const [isGrantingPoints, setIsGrantingPoints] = useState(false);
    const [isPointsDialogOpen, setIsPointsDialogOpen] = useState(false);
    const [pointsInput, setPointsInput] = useState("1");
    const [pointsInputError, setPointsInputError] = useState("");
    const [bars, setBars] = useState<IBar[]>([]);
    const [isBarsLoading, setIsBarsLoading] = useState(false);
    const [selectedPointsBarId, setSelectedPointsBarId] = useState("manual");

    const { show } = useContext(ConfirmContext);

    const showNotice = (title: string, message: string) => {
        show({
            title,
            message,
            actions: [
                {
                    title: 'OK',
                    role: 'cancel',
                    style: { color: '#fff' }
                }
            ]
        });
    }

    useEffect(() => {

        Promise.all([
            CustomerService.getCustomer(params.id),
            CustomerService.getReports(params.id)
        ]).then(([customer, report]) => {
            setCustomer(customer);
            setReport(report)
        })

    }, [params.id])

    useEffect(() => {
        let isMounted = true;
        setIsBarsLoading(true);

        BarsService.getBars()
            .then((bars) => {
                if (!isMounted) {
                    return;
                }

                const sortedBars = (bars || [])
                    .filter((bar) => !!bar?.id)
                    .slice()
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

                setBars(sortedBars);
            })
            .catch(() => {
                if (!isMounted) {
                    return;
                }

                setBars([]);
            })
            .finally(() => {
                if (isMounted) {
                    setIsBarsLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const deleteEntry = () => {
        CustomerService.deleteCustomer(customer.id);
        history.goBack();
    }

    const onGivePointsClick = () => {
        if (!customer?.id) {
            showNotice('Unable to add points', 'Customer details are still loading. Please try again.');
            return;
        }

        setPointsInput("1");
        setPointsInputError("");
        setSelectedPointsBarId("manual");
        setIsPointsDialogOpen(true);
    }

    const onClosePointsDialog = () => {
        if (isGrantingPoints) {
            return;
        }
        setIsPointsDialogOpen(false);
    }

    const onSubmitPointsDialog = async () => {
        const points = Number(pointsInput);
        if (!Number.isFinite(points) || points <= 0 || Math.floor(points) !== points) {
            setPointsInputError('Please enter a positive whole number.');
            return;
        }

        setPointsInputError("");

        setIsGrantingPoints(true);
        try {
            const result = await CustomerService.addManualPoints(customer.id, points, selectedPointsBarId);
            const updatedReport = await CustomerService.getReports(customer.id);
            setReport(updatedReport);

            showNotice(
                'Points granted',
                `Successfully added ${result.created} point${result.created === 1 ? '' : 's'} to this customer.`
            );
            setIsPointsDialogOpen(false);
        } catch (error) {
            showNotice(
                'Unable to add points',
                error instanceof Error ? error.message : 'Something went wrong while granting points.'
            );
        } finally {
            setIsGrantingPoints(false);
        }
    }

    const onDeleteClick = () => {
        show({
            title: 'Delete customer',
            message: `Are you sure you want to delete ${customer?.firstName} ${customer.lastName}`,
            actions: [
                {
                    title: 'No',
                    role: 'cancel',
                    style: { color: '#fff' }
                },
                {
                    title: 'Yes',
                    role: 'destructive',
                    style: { backgroundColor: '#DF2727', color: '#fff' },
                    handler: deleteEntry
                }
            ]
        });
    }



    return (
        <CustomerDetailsPage
            entry={customer}
            report={report}
            onDeleteClick={onDeleteClick}
            onGivePointsClick={onGivePointsClick}
            isGrantingPoints={isGrantingPoints}
            isPointsDialogOpen={isPointsDialogOpen}
            pointsInput={pointsInput}
            pointsInputError={pointsInputError}
            pointsBars={bars}
            isBarsLoading={isBarsLoading}
            selectedPointsBarId={selectedPointsBarId}
            onPointsInputChange={setPointsInput}
            onPointsBarChange={setSelectedPointsBarId}
            onClosePointsDialog={onClosePointsDialog}
            onSubmitPointsDialog={onSubmitPointsDialog}
        />
    )
}
